import {getUserToken} from '../helper';
import {resolveAPIRoute} from '../routes';

interface ResponseType {
  arrayBuffer: ArrayBuffer;
  blob: Blob;
  text: string;
  json: {
    data: unknown;
    message: string;
    succeed: boolean;
    meta?: {
      limit: number;
      order: number;
      page: number;
      sort: string;
      total: number;
    };
  };
}

export const TOKEN_HEADER = 'X-Auth-Token';

/** Custom error class for storing HTTP error codes during the Fetch API calls. */
export class HTTPError extends Error {
  /**
   * @param code - HTTP error code
   * @param cause - The error response or object
   * @param timedOut - Request failed due to timeout
   * @param networkIssue - Request failed due to network connectivity
   */
  constructor(
    public code?: number,
    public cause?: any | ResponseType['json'],
    public timedOut?: boolean,
    public networkIssue?: boolean
  ) {
    super();
  }
}

/**
 * Sends fetch requests while handling errors.
 *
 * @param requestArgs - The additional arguments for the `Request` object's options.
 * @param selector - The callback to modify the received data.
 * @param headers - The callback to get the response headers.
 * @param timeout - In milliseconds
 * @param type - The method to use for getting representation of the response body.
 *
 * @typeParam {@link R} - The custom type of the response.
 *
 * @throws - {@link HTTPError}
 * When response's status code is not in the range 200–299.
 *
 * @bug Next.js's Request Memoization does not work while using cookies.
 * @see https://github.com/vercel/next.js/issues/59010
 */
export async function baseFetch<
  R,
  T extends keyof ResponseType = 'json',
  D = ResponseType[T] extends R ? ResponseType[T] : R
>(
  url: URL | string,
  requestArgs: RequestInit = {},
  selector?: (data: D) => D,
  headers?: (headers: Headers) => any,
  timeout = Number(process.env.NEXT_PUBLIC_DEFAULT_REQUEST_TIMEOUT) * 1e3,
  type: T = 'json' as T
): Promise<D> {
  let error: any;
  let status: number | undefined;
  let networkIssue: boolean | undefined;
  const isJson = type === 'json';
  const isBodyString = requestArgs.body === 'string';
  const isBodyFormData = requestArgs.body instanceof FormData;
  const token = (await getUserToken()) || '';
  const controller = new AbortController();
  const timerId =
    timeout !== Infinity
      ? setTimeout(() => controller.abort(), timeout)
      : undefined;

  try {
    /**
     * Using `new Request()` to build the request object causes Next.js
     * time-based cache revalidation to not work with the `fetch()`.
     * @see https://github.com/vercel/next.js/issues/69097
     */
    const response = await fetch(url, {
      signal: controller.signal,
      credentials: 'same-origin',
      ...requestArgs,
      body:
        !isBodyString && !isBodyFormData
          ? JSON.stringify(requestArgs.body)
          : requestArgs.body,
      headers: {
        ...(isJson ? {Accept: 'application/json'} : {}),
        ...(isBodyFormData
          ? {}
          : {'Content-Type': isBodyString ? 'text/plain' : 'application/json'}),
        [TOKEN_HEADER]: token,
        ...requestArgs.headers
      }
    });

    clearTimeout(timerId);
    status = response.status;
    if (response.ok) {
      headers?.(response.headers);
      const data = await response[type]();
      return selector ? selector(data) : data;
    } else if (status >= 400) {
      try {
        headers?.(response.headers);
        if (isJson) {
          error = await response.json();
        }
        if (!error) {
          error = await response.text();
        }
      } catch (_error) {
        error = _error;
      }
    } else {
      error = Error('Response is not handled');
    }
  } catch (_error) {
    error = _error;

    if (
      typeof window !== 'undefined' &&
      !window.navigator.onLine &&
      !controller.signal.aborted
    )
      networkIssue = true;
  }

  if (process.env.LOG_API_ERRORS === 'true') {
    url = url.toString();
    const isProfileUrl = url === resolveAPIRoute('base.profile').toString();
    // There is no need to log the user authentication errors
    if (!isProfileUrl || (isProfileUrl && status !== 401)) {
      console.error(
        `The following error occurred during fetching the URL: '${url}'`
      );
      console.error(error);
    }
  }

  throw new HTTPError(status, error, controller.signal.aborted, !!networkIssue);
}

/** Injects the given cache tags to the given Next.js `Request` object's options in place. */
export function injectCacheTags(
  tags: NonNullable<NextFetchRequestConfig['tags']>,
  requestArgs: RequestInit = {}
) {
  if (typeof window === 'undefined') {
    if (requestArgs.next) {
      if (requestArgs.next.tags) {
        requestArgs.next.tags.push(...tags);
      } else {
        requestArgs.next.tags = tags;
      }
    } else {
      requestArgs.next = {tags};
    }
  }

  return requestArgs;
}
